import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import { Search, X, FileText, Briefcase, Package, ArrowRight, Command } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'

const SearchBar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef(null)
  const navigate = useNavigate()
  const { language } = useLanguage()

  // Search data - simulate real search
  const searchData = [
    // Services
    { type: 'service', title: 'Website Development', titleSq: 'Zhvillim Website', path: '/services', icon: Briefcase },
    { type: 'service', title: 'E-Commerce Solutions', titleSq: 'Zgjidhje E-Commerce', path: '/services', icon: Briefcase },
    { type: 'service', title: 'Mobile Apps', titleSq: 'Aplikacione Mobile', path: '/services', icon: Briefcase },
    { type: 'service', title: 'SEO Marketing', titleSq: 'Marketing SEO', path: '/services', icon: Briefcase },
    { type: 'service', title: 'Branding & Design', titleSq: 'Branding & Dizajn', path: '/services', icon: Briefcase },
    
    // Portfolio
    { type: 'portfolio', title: 'Contenta Marketing Platform', titleSq: 'Platforma Contenta Marketing', path: '/portfolio', icon: Package },
    { type: 'portfolio', title: 'E-Commerce Fashion Store', titleSq: 'Dyqan Online Fashion', path: '/portfolio', icon: Package },
    { type: 'portfolio', title: 'Restaurant Website', titleSq: 'Website Restorant', path: '/portfolio', icon: Package },
    
    // Pages
    { type: 'page', title: 'About Us', titleSq: 'Rreth Nesh', path: '/about', icon: FileText },
    { type: 'page', title: 'Contact', titleSq: 'Kontakt', path: '/contact', icon: FileText },
    { type: 'page', title: 'Consultation', titleSq: 'Konsultim', path: '/consultation', icon: FileText },
    { type: 'page', title: 'Apply for Job', titleSq: 'Apliko për Punë', path: '/apply', icon: FileText },
    { type: 'page', title: 'Blog', titleSq: 'Blog', path: '/blog', icon: FileText },
  ]

  // Keyboard shortcut - Ctrl+K or Cmd+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen(true)
      }
      
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Search logic
  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      return
    }

    const lowerQuery = query.toLowerCase()
    const filtered = searchData.filter(item => {
      const title = language === 'sq' ? item.titleSq : item.title
      return title.toLowerCase().includes(lowerQuery)
    })

    setResults(filtered.slice(0, 8)) // Show max 8 results
    setSelectedIndex(0)
  }, [query, language])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault()
        handleSelect(results[selectedIndex])
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, results, selectedIndex])

  const handleSelect = (item) => {
    navigate(item.path)
    setIsOpen(false)
    setQuery('')
  }

  const handleClose = () => {
    setIsOpen(false)
    setQuery('')
    setResults([])
  }

  return (
    <>
      {/* Trigger Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className="hidden md:flex items-center gap-2 px-4 py-2 bg-bronze-900/50 border border-gold-400/20 rounded-lg text-beige-300 hover:border-gold-400/40 transition-all"
      >
        <Search className="w-4 h-4" />
        <span className="text-sm">Search...</span>
        <div className="flex items-center gap-1 ml-2 px-2 py-0.5 bg-bronze-800 rounded text-xs">
          <Command className="w-3 h-3" />
          <span>K</span>
        </div>
      </motion.button>

      {/* Mobile trigger */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className="md:hidden p-2 text-cream hover:text-gold-400 transition-colors"
      >
        <Search className="w-5 h-5" />
      </motion.button>

      {/* Search Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 sm:p-6"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl max-h-[90vh] overflow-hidden"
            >
              <div className="bg-gradient-to-br from-bronze-900 to-bronze-950 rounded-2xl shadow-2xl border border-gold-400/30 overflow-hidden">
                {/* Input */}
                <div className="flex items-center gap-3 p-4 border-b border-gold-400/20">
                  <Search className="w-5 h-5 text-gold-400" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={language === 'sq' ? 'Kërko shërbime, projekte, faqe...' : 'Search services, projects, pages...'}
                    className="flex-1 bg-transparent border-none outline-none text-cream placeholder:text-beige-400 text-lg"
                  />
                  <button
                    onClick={handleClose}
                    className="p-1 hover:bg-bronze-800 rounded transition-colors"
                  >
                    <X className="w-5 h-5 text-beige-300" />
                  </button>
                </div>

                {/* Results */}
                <div className="max-h-[50vh] sm:max-h-96 overflow-y-auto">
                  {query.length < 2 ? (
                    <div className="p-8 text-center text-beige-400">
                      <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">
                        {language === 'sq' 
                          ? 'Shkruaj të paktën 2 shkronja për të filluar kërkimin'
                          : 'Type at least 2 characters to start searching'
                        }
                      </p>
                    </div>
                  ) : results.length === 0 ? (
                    <div className="p-8 text-center text-beige-400">
                      <p className="text-sm">
                        {language === 'sq' 
                          ? 'Nuk u gjetën rezultate për "' + query + '"'
                          : 'No results found for "' + query + '"'
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="p-2">
                      {results.map((item, index) => {
                        const Icon = item.icon
                        const title = language === 'sq' ? item.titleSq : item.title
                        
                        return (
                          <motion.button
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            onClick={() => handleSelect(item)}
                            className={`w-full flex items-center gap-4 p-3 rounded-lg text-left transition-all ${
                              index === selectedIndex
                                ? 'bg-gradient-gold text-white'
                                : 'hover:bg-bronze-800 text-cream'
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              index === selectedIndex
                                ? 'bg-white/20'
                                : 'bg-bronze-800'
                            }`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold truncate">{title}</div>
                              <div className={`text-xs capitalize ${
                                index === selectedIndex ? 'text-white/80' : 'text-beige-400'
                              }`}>
                                {item.type}
                              </div>
                            </div>

                            <ArrowRight className="w-4 h-4 flex-shrink-0 opacity-50" />
                          </motion.button>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-3 border-t border-gold-400/20 bg-bronze-950/50 text-xs text-beige-400">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 bg-bronze-800 rounded">↑</kbd>
                      <kbd className="px-1.5 py-0.5 bg-bronze-800 rounded">↓</kbd>
                      Navigate
                    </span>
                    <span className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 bg-bronze-800 rounded">Enter</kbd>
                      Select
                    </span>
                  </div>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-bronze-800 rounded">Esc</kbd>
                    Close
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default SearchBar
